import { FormattedText } from "@tm/static";
import { theme } from "@tm/utility/global";
import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  BenefitCellContent,
  BenefitIcon,
  BenefitLabel,
  CellValueIcon,
  DashValue,
  FixedTooltip,
  InfoTrigger,
  TableCell,
  TableRow,
} from "./ComparePlans.styles";
import { IComparePlansBenefit } from "./types";

const CELL_VALUE_STYLE: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 700,
};

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke={theme.greyText6} strokeWidth="1.2" />
    <path
      d="M7 6.2v4M7 4.2v.8"
      stroke={theme.greyText6}
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

interface CompareTableRowProps {
  benefit: IComparePlansBenefit;
  planIds: string[];
  recommendedId: string | null;
  rowIndex: number;
}

const CompareTableRow = ({
  benefit,
  planIds,
  recommendedId,
  rowIndex,
}: CompareTableRowProps) => {
  const { label, data } = benefit || {};
  const isAlternate = rowIndex % 2 === 0;
  const labelText = label?.text || "";
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const iconRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = () => {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.top + rect.height / 2,
      left: rect.right + 2,
    });
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <TableRow $alternate={isAlternate}>
      <TableCell $isFirst $alternate={isAlternate}>
        <BenefitCellContent>
          {label?.icon && <BenefitIcon src={label.icon} alt="" />}
          <BenefitLabel>{labelText}</BenefitLabel>
          {label?.tooltip && (
            <>
              <InfoTrigger
                ref={iconRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                aria-label={`Info about ${labelText}`}
              >
                <InfoIcon />
              </InfoTrigger>
              {isHovered &&
                createPortal(
                  <FixedTooltip
                    style={{
                      top: `${tooltipPos.top}px`,
                      left: `${tooltipPos.left}px`,
                      transform: "translateY(-50%)",
                    }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    {label.tooltip}
                  </FixedTooltip>,
                  document.body
                )}
            </>
          )}
        </BenefitCellContent>
      </TableCell>

      {planIds.map((id) => {
        const cellData = data?.[id];
        const hasIcon = !!cellData?.icon;
        const hasTitle = !!cellData?.title;

        return (
          <TableCell
            key={id}
            $isRecommended={id === recommendedId}
            $alternate={isAlternate}
          >
            {hasIcon && <CellValueIcon src={cellData.icon} alt="" />}
            {hasTitle && !hasIcon && (
              <FormattedText
                value={cellData.title}
                baseStyle={CELL_VALUE_STYLE}
              />
            )}
            {!hasIcon && !hasTitle && <DashValue>-</DashValue>}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default CompareTableRow;
